import classNames from "classnames"
import React, {
  ComponentProps,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

import PillButton from "./PillButton"
import { useAddBulkToCart } from "../../../../cart/hooks"
import { CartContext } from "../../../../cart/store"
import { Color } from "../../../../constants/V2/color"
import { trackAddToCart } from "../../../../utils/analytics"
import Checkmark from "../Icons/CheckmarkIcon"
import PlusIcon from "../Icons/PlusIcon"
import Typography from "../Typography"

import { SelectedPurchasableItem } from "@hooks/V2/storyblok"
import { formatPrice } from "@utils/price"
import { getAccentColor, getFillColorClass } from "@utils/V2/color"

type Props = {
  size?: "small" | "medium" | "large"
  hideIcon?: boolean
  className?: string
  selectedPurchasableItems: SelectedPurchasableItem[]
  buttonColor?: Color
  price?: number
  overrideTextColor?: Color
  openCartPreview?: boolean
  fontSize?: ComponentProps<typeof Typography>["size"]
  iconSize?: number
  waitlistLink?: string
  onClick?: () => void
  disabled?: boolean
  alreadyInCart?: boolean
}

const BulkAddToCartButton = ({
  size = "large",
  hideIcon,
  className,
  selectedPurchasableItems,
  buttonColor = Color.Blue,
  price,
  overrideTextColor = getAccentColor(buttonColor),
  openCartPreview = true,
  iconSize = 18,
  disabled = false,
  alreadyInCart,
  onClick,
}: Props) => {
  const cartContext = useContext(CartContext)
  const addBulkToCart = useAddBulkToCart()

  const iconStyles =
    "group-hover:fill-white group-hover:rotate-90 group-focus-visible:rotate-90 transform transition duration-300 ease-in-out"

  const [cartDisabled, setCartDisabled] = useState(false)
  const [handlingAddToCart, setHandlingAddToCart] = useState(false)

  const handleAddToCart = useCallback(async () => {
    setHandlingAddToCart(true)
    const addedCartItems = await addBulkToCart(selectedPurchasableItems)
    for (const item of addedCartItems) {
      trackAddToCart(item)
    }

    setHandlingAddToCart(false)
    if (openCartPreview) {
      cartContext.setCartPreviewOpen(true)
    }

    if (onClick) {
      onClick()
    }
  }, [
    addBulkToCart,
    selectedPurchasableItems,
    openCartPreview,
    onClick,
    cartContext,
  ])

  useEffect(() => {
    setCartDisabled(cartContext.cartDisabled)
  }, [cartContext.cartDisabled])

  if (alreadyInCart) {
    return (
      <PillButton
        text="In cart"
        size={size}
        style="bordered"
        icon={<Checkmark className="stroke-blue group-hover:stroke-white" />}
        hideIcon={hideIcon}
        color={Color.Blue}
        className={className}
        disabled={disabled}
        overrideTextColor={Color.Blue}
      />
    )
  }

  return (
    <PillButton
      text={"Add to cart"}
      secondaryText={
        price
          ? formatPrice(price, {
              hasThousandsSeparator: true,
              shouldShowDecimals: true,
            })
          : ""
      }
      iconPosition="right"
      size={size}
      style="solid"
      icon={
        <PlusIcon
          className={classNames(
            getFillColorClass(overrideTextColor),
            iconStyles
          )}
          height={iconSize}
          width={iconSize}
        />
      }
      hideIcon={hideIcon}
      color={buttonColor}
      onClick={handleAddToCart}
      disabled={disabled || cartDisabled || handlingAddToCart}
      className={className}
      data-testid="add-to-cart"
      overrideTextColor={overrideTextColor}
    />
  )
}

export default BulkAddToCartButton
